import React, { useEffect, useState } from 'react';
import { get, post } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';

export default function AccountSettings({ onUpdated }) {
  const { confirmAccountUpdate } = useAuth();
  const [current, setCurrent] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '' });
  const [channel, setChannel] = useState('phone');
  const [pending, setPending] = useState(null);
  const [code, setCode] = useState('');
  const [seconds, setSeconds] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  useEffect(() => {
    let active = true;
    get('/auth/me').then(response => {
      if (!active) return;
      setCurrent(response.user);
      setForm({ name: response.user.name || '', email: response.user.email || '', phone: response.user.phone || '' });
      setChannel(response.user.phone ? 'phone' : 'email');
    }).catch(() => { if (active) setError('Unable to load account details. Reload to try again.'); });
    return () => { active = false; };
  }, []);
  useEffect(() => {
    if (!seconds) return undefined;
    const timer = setTimeout(() => setSeconds(seconds - 1), 1000);
    return () => clearTimeout(timer);
  }, [seconds]);
  const request = async event => {
    event?.preventDefault(); setBusy(true); setError(''); setMessage('');
    const details = pending || { ...form, channel };
    try {
      const response = await post('/customers/me/details/request', details);
      setPending(details); setCode(''); setSeconds(response.retryAfter || 60);
      setMessage('Code sent to your ' + (details.channel === 'email' ? 'current verified email.' : 'current mobile number.'));
    } catch (e) { setError(e.response?.data?.message || e.message || 'Unable to send a code.'); }
    finally { setBusy(false); }
  };
  const confirm = async event => {
    event.preventDefault(); setBusy(true); setError(''); setMessage('');
    try {
      const response = await confirmAccountUpdate({ ...pending, code });
      const next = { user: response.user };
      setCurrent(next.user);
      setForm({ name: next.user.name || '', email: next.user.email || '', phone: next.user.phone || '' });
      setPending(null); setCode(''); setMessage(response.message || 'Details updated successfully.');
      onUpdated?.(next.user);
    } catch (e) { setError(e.response?.data?.message || e.message || 'Unable to update account details.'); }
    finally { setBusy(false); }
  };
  if (!current) return <p role={error ? 'alert' : 'status'}>{error || 'Loading account details...'}</p>;
  return <form onSubmit={pending ? confirm : request} className="space-y-4 rounded-xl border bg-white p-6">
    <h3 className="text-lg font-semibold">Update account details</h3>
    <p className="text-sm text-slate-500">Confirm an OTP sent to your verified email or current mobile number before saving changes.</p>
    <fieldset disabled={busy || Boolean(pending)} className="space-y-4">
      <label className="block text-sm font-medium">Full name
        <input required minLength={2} maxLength={150} autoComplete="name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="mt-2 w-full rounded-lg border p-3" />
      </label>
      <label className="block text-sm font-medium">Email address
        <input type="email" required autoComplete="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="mt-2 w-full rounded-lg border p-3" />
      </label>
      <label className="block text-sm font-medium">Mobile number
        <input type="tel" required autoComplete="tel" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="mt-2 w-full rounded-lg border p-3" />
      </label>
      <label className="block text-sm font-medium">Receive OTP through
        <select value={channel} onChange={e => setChannel(e.target.value)} className="mt-2 w-full rounded-lg border bg-white p-3">
          <option value="phone" disabled={!current.phone}>Current mobile number {current.phone}</option>
          <option value="email" disabled={!current.emailVerifiedAt}>Verified email {current.emailVerifiedAt ? current.email : ''}</option>
        </select>
      </label>
    </fieldset>
    {pending && <label className="block text-sm font-medium">Verification code
      <input required inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} value={code} disabled={busy}
        onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} className="mt-2 w-full rounded-lg border p-3 text-center tracking-widest" />
    </label>}
    {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
    {message && <p role="status" className="text-sm text-emerald-700">{message}</p>}
    <Button type="submit" disabled={busy || (pending ? code.length !== 6 : seconds > 0 || (!current.phone && !current.emailVerifiedAt))}>
      {busy ? 'Please wait...' : pending ? 'Verify and update' : seconds ? 'Send again in ' + seconds + 's' : 'Send verification code'}
    </Button>
    {pending && <div className="flex flex-wrap gap-2">
      <Button type="button" variant="ghost" disabled={busy || seconds > 0} onClick={request}>{seconds ? 'Resend in ' + seconds + 's' : 'Resend code'}</Button>
      <Button type="button" variant="ghost" disabled={busy} onClick={() => { setPending(null); setCode(''); setError(''); setMessage(''); }}>Edit details</Button>
    </div>}
  </form>;
}
